///
/// EarthBrowser - Feature Flags Slice
///
/// Redux slice for client-side feature flag state management.
/// Fetches evaluated feature flags from the API and caches them.
///
/// Created by Matt Giger on 4/4/2025
/// Updated: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { EvaluatedFlags, EvaluatedFlagsResponse } from "@shared-types/feature-flags"

const API_URL = import.meta.env.VITE_API_URL || ''

interface FeatureFlagsState {
	/** Map of flag key to boolean value */
	flags: EvaluatedFlags
	/** Loading state for initial fetch */
	loading: boolean
	/** Error message if fetch failed */
	error: string | null
	/** Timestamp of last successful fetch */
	lastFetched: number | null
	/** TTL hint from server in seconds */
	ttl: number
	/** Local overrides for development/testing */
	localOverrides: EvaluatedFlags
}

const initialState: FeatureFlagsState = {
	flags: {},
	loading: false,
	error: null,
	lastFetched: null,
	ttl: 300, // Default 5 minutes
	localOverrides: {},
}

/**
 * Async thunk to fetch evaluated feature flags from the API
 */
export const fetchFeatureFlags = createAsyncThunk(
	'featureFlags/fetch',
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetch(`${API_URL}/api/features`, {
				credentials: 'include',
			})
			if (!response.ok) {
				throw new Error(`Failed to fetch feature flags: ${response.status}`)
			}
			return await response.json() as EvaluatedFlagsResponse
		} catch (error) {
			console.error('[FeatureFlags] Fetch error:', error)
			return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
		}
	}
)

const featureFlagsSlice = createSlice({
	name: "featureFlags",
	initialState,
	reducers: {
		/**
		 * Set a local override for a feature flag (development/testing)
		 * Local overrides take precedence over server values
		 */
		setLocalOverride: (state, action: PayloadAction<{ key: string; value: boolean }>) => {
			state.localOverrides[action.payload.key] = action.payload.value
		},

		/**
		 * Remove a local override for a feature flag
		 */
		removeLocalOverride: (state, action: PayloadAction<string>) => {
			delete state.localOverrides[action.payload]
		},

		/**
		 * Clear all local overrides
		 */
		clearLocalOverrides: (state) => {
			state.localOverrides = {}
		},

		/**
		 * Force a refetch on next access
		 */
		invalidateFlags: (state) => {
			state.lastFetched = null
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchFeatureFlags.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(fetchFeatureFlags.fulfilled, (state, action) => {
				state.flags = action.payload.flags
				state.loading = false
				state.lastFetched = Date.now()
				if (action.payload.ttl) {
					state.ttl = action.payload.ttl
				}
			})
			.addCase(fetchFeatureFlags.rejected, (state, action) => {
				state.loading = false
				state.error = action.payload as string
			})
	},
})

export const {
	setLocalOverride,
	removeLocalOverride,
	clearLocalOverrides,
	invalidateFlags,
} = featureFlagsSlice.actions

export default featureFlagsSlice.reducer

// ============================================================================
// SELECTORS
// ============================================================================

interface FeatureFlagsRootState {
	featureFlags: FeatureFlagsState
}

/**
 * Select a single feature flag value by key
 * Local overrides take precedence over server values
 */
export const selectFeatureFlag = (key: string) => (state: FeatureFlagsRootState): boolean => {
	// Check local overrides first
	if (key in state.featureFlags.localOverrides) {
		return state.featureFlags.localOverrides[key]
	}
	// Fall back to server value, default to false if not found
	return state.featureFlags.flags[key] ?? false
}

/**
 * Select all feature flags (merged with local overrides)
 */
export const selectAllFlags = (state: FeatureFlagsRootState): EvaluatedFlags => {
	return {
		...state.featureFlags.flags,
		...state.featureFlags.localOverrides,
	}
}

/**
 * Select the loading state
 */
export const selectFlagsLoading = (state: FeatureFlagsRootState): boolean => {
	return state.featureFlags.loading
}

/**
 * Select the error state
 */
export const selectFlagsError = (state: FeatureFlagsRootState): string | null => {
	return state.featureFlags.error
}

/**
 * Select whether flags need to be refetched
 */
export const selectFlagsNeedRefetch = (state: FeatureFlagsRootState): boolean => {
	const { lastFetched, ttl } = state.featureFlags
	if (!lastFetched) return true
	const ttlMs = ttl * 1000
	return Date.now() - lastFetched > ttlMs
}

/**
 * Select local overrides for debugging
 */
export const selectLocalOverrides = (state: FeatureFlagsRootState): EvaluatedFlags => {
	return state.featureFlags.localOverrides
}
